import { FC, useMemo, useRef, useState } from 'react';
import { Trans } from 'next-i18next';
import cn from 'classnames';
import styled from 'styled-components';

import { useNotices } from '@/features/notices/useNotices';
import { media } from '@/shared/components';
import { Reel, Reels } from '@/shared/components/reels';
import { COLORS } from '@/shared/constants/colors';
import { ANDROID_APP_LINK, IOS_APP_LINK } from '@/shared/constants/constants';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { Button } from '@/shared/uikit/Button';
import { Icon } from '@/shared/uikit/Icon';
import { CarouselRef } from '@/shared/uikitV2/antd/Carousel';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { copy } from '@/shared/utils/misc';
import { ModalSlideContainer } from '@/widgets/features/ModalSlideContainer';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { ScenarioTestGuideModalProps } from '@/widgets/modals/types';

export const ScenarioTestGuideModal: FC<ScenarioTestGuideModalProps> = () => {
  const modals = useModalsStore();
  const { t } = useTranslation('scenarios', {
    keyPrefix: 'guide',
  });

  const [currentStep, setCurrentStep] = useState(0);
  const reelsRef = useRef<CarouselRef>(null);
  const { success } = useNotices();
  const isDesktop = useIsDesktop();

  const goToNext = () => reelsRef.current?.next();
  const goBack = () => reelsRef.current?.prev();
  const handleClose = () => modals.closeModal();
  const handleBeforeChange = (_: number, next: number) => setCurrentStep(next);

  const device = isDesktop ? 'desktop' : 'mobile';
  const imageSrcPath = `/assets/widgets/modals/scenarioTestGuide/${device}`;

  const reels: Array<Reel> = useMemo(
    () => [
      {
        title: (
          <ReelTitle>
            <Trans
              i18nKey={t('first-reel')}
              components={{
                ios: (
                  <CopyLinkText
                    onClick={() => {
                      copy(IOS_APP_LINK);
                      success(t('linkCopiedMessage'));
                    }}
                  />
                ),
                android: (
                  <CopyLinkText
                    onClick={() => {
                      copy(ANDROID_APP_LINK);
                      success(t('linkCopiedMessage'));
                    }}
                  />
                ),
              }}
            />
          </ReelTitle>
        ),
        imageSrc: `${imageSrcPath}/1-link.png`,
      },

      {
        title: (
          <ReelTitle>
            <Trans i18nKey={t('second-reel')} components={{ br: <br /> }} />
          </ReelTitle>
        ),
        imageSrc: `${imageSrcPath}/2-ask.png`,
      },

      {
        title: (
          <ReelTitle>
            <Trans i18nKey={t('third-reel')} components={{ br: <br /> }} />
          </ReelTitle>
        ),
        imageSrc: `${imageSrcPath}/3-time.png`,
      },

      {
        title: (
          <ReelTitle>
            <Trans i18nKey={t('fourth-reel')} />
          </ReelTitle>
        ),
        imageSrc: `${imageSrcPath}/4-notification.png`,
      },

      {
        title: (
          <ReelTitle>
            <Trans i18nKey={t('fifth-reel')} components={{ br: <br /> }} />
          </ReelTitle>
        ),
        imageSrc: `${imageSrcPath}/5-asset.png`,
      },

      {
        title: (
          <ReelTitle>
            <Trans i18nKey={t('sixth-reel')} components={{ br: <br /> }} />
          </ReelTitle>
        ),
        imageSrc: `${imageSrcPath}/6-triggered.png`,
      },
    ],
    [success, imageSrcPath],
  );

  const isLastStep = currentStep === reels.length - 1;
  const isFirstStep = currentStep === 0;
  const dots = isDesktop ? (
    Array(reels.length)
      .fill(1)
      .map((_, i) => (
        <button
          key={i}
          className={cn('dot-button', { current: currentStep === i })}
          onClick={() => reelsRef.current?.goTo(i, false)}
        />
      ))
  ) : (
    <>
      <button className="caret-button" type="button">
        <Icon size="s16" name="caret-left" onClick={goBack} />
      </button>
      {Array(reels.length)
        .fill(1)
        .map((_, i) => (
          <div key={i} className={cn('dot', { current: currentStep === i })} />
        ))}
      <button className="caret-button" type="button">
        <Icon size="s16" name="caret-right" onClick={goToNext} />
      </button>
    </>
  );

  return (
    <ModalSlideContainer
      title={t('title')}
      footer={null}
      open
      onCancel={handleClose}
      width={600}
      drawerHeight="100vh"
      drawerStyles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        },
      }}
    >
      <ReelsContainer>
        <Reels
          dots={false}
          reels={reels}
          ref={reelsRef}
          beforeChange={handleBeforeChange}
          titleLevel={5}
        />
      </ReelsContainer>
      <Footer>
        <div className="dot-block">{dots}</div>
        <div className="buttons">
          {isDesktop && (
            <Button
              type="default"
              className="button-back"
              onClick={isFirstStep ? handleClose : goBack}
            >
              {isFirstStep ? t('buttons.close') : t('buttons.back')}
            </Button>
          )}
          <Button className="button-next" onClick={isLastStep ? handleClose : goToNext}>
            {isLastStep ? t('buttons.gotIt') : t('buttons.next')}
          </Button>
        </div>
      </Footer>
    </ModalSlideContainer>
  );
};

// ===== styles =====

const ReelsContainer = styled.div`
  padding: 16px 16px 0 16px;

  ${media.desktop} {
    border-top: 1px solid ${COLORS.systemDark100};
    border-bottom: 1px solid ${COLORS.systemDark100};
    padding: 24px;
  }

  .ant-flex {
    padding: 0 !important;
  }
`;

const ReelTitle = styled.div`
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 16px;
  font-weight: 400;

  ${media.desktop} {
    font-size: 18px;
    line-height: 22px;
    font-weight: 600;
  }
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding-inline: 16px;
  padding-bottom: 16px;
  gap: 28px;

  ${media.desktop} {
    padding-top: 16px;
    padding-inline: 24px;
    flex-direction: row;
  }

  .dot-block {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .dot,
  .dot-button {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: none;
    background-color: ${COLORS.systemDark50A};
    cursor: pointer;
  }

  .dot.current,
  .dot-button.current {
    background-color: ${COLORS.systemDark300};
  }

  .caret-button {
    width: 24px;
    height: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${COLORS.systemLight200};
    border: none;
    border-radius: 50%;
  }

  .buttons {
    height: 44px;
    width: 100%;

    ${media.desktop} {
      display: flex;
      gap: 16px;
      width: fit-content;
    }
  }

  .button-back {
    height: inherit;
  }

  .button-next {
    height: inherit;
    width: 100%;

    ${media.desktop} {
      width: 70px;
    }
  }
`;

const CopyLinkText = styled.span`
  color: ${COLORS.systemSky200};
  cursor: pointer;
`;
